import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiClock,
  FiMessageCircle,
  FiPhone,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUser,
  FiX,
} from "react-icons/fi";
import saleAgentService from "../../api/services/saleAgentService";
import { formatCallDuration, toDatetimeLocalValue } from "../../utils/callDuration";
import { ListSkeleton, TableSkeleton } from "../../components/common/skeletons";
import PaginationBar from "../../components/common/PaginationBar";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none w-full";

const ALLOWED = ["Admin", "Developer", "CSR", "CSR Team Lead"];
const MARITAL_OPTIONS = ["Unmarried", "Divorced", "Married", "Widowed", "not mentioned"];
const GENDER_OPTIONS = ["male", "female", "not mentioned"];
const LEADS_PAGE_SIZE = 20;

function whatsAppUrl(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function emptyLeadForm() {
  return {
    fullName: "",
    contactNumber: "",
    maritalStatus: "not mentioned",
    gender: "male",
    assignedAgentId: "",
    countryCity: "",
    notes: "",
  };
}

function ContactModal({ lead, agents, onClose, onSaved }) {
  const [form, setForm] = useState({
    fullName: lead.fullName || "",
    contactNumber: lead.contactNumber || "",
    maritalStatus: lead.maritalStatus || "not mentioned",
    gender: lead.gender || "male",
    assignedAgentId: String(lead.assignedAgentId || ""),
    countryCity: lead.countryCity || "",
    notes: lead.notes || "",
  });
  const [calls, setCalls] = useState([]);
  const [callLoading, setCallLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [callForm, setCallForm] = useState({
    hours: 0,
    minutes: 5,
    seconds: 0,
    started_at: toDatetimeLocalValue(),
    notes: "",
  });

  const loadCalls = useCallback(async () => {
    setCallLoading(true);
    try {
      const data = await saleAgentService.getCallHistory(lead.id);
      setCalls(data.calls || []);
    } catch {
      setCalls([]);
    } finally {
      setCallLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const saveContact = async (withCall = false) => {
    setSaving(true);
    try {
      const payload = {
        fullName: form.fullName,
        contactNumber: form.contactNumber,
        maritalStatus: form.maritalStatus,
        gender: form.gender,
        countryCity: form.countryCity,
        notes: form.notes,
        assignedAgentId: form.assignedAgentId ? Number(form.assignedAgentId) : undefined,
      };
      if (withCall) {
        payload.log_call = true;
        payload.hours = callForm.hours;
        payload.minutes = callForm.minutes;
        payload.seconds = callForm.seconds;
        payload.started_at = callForm.started_at
          ? new Date(callForm.started_at).toISOString().slice(0, 19).replace("T", " ")
          : undefined;
        payload.notes = callForm.notes;
      }
      await saleAgentService.contactLead(lead.id, payload);
      toast.success(withCall ? "Contact saved and call logged" : "Contact details saved");
      onSaved?.();
      if (withCall) loadCalls();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const logManualCall = async () => {
    setSaving(true);
    try {
      await saleAgentService.logCall(lead.id, {
        hours: callForm.hours,
        minutes: callForm.minutes,
        seconds: callForm.seconds,
        started_at: callForm.started_at
          ? new Date(callForm.started_at).toISOString().slice(0, 19).replace("T", " ")
          : undefined,
        notes: callForm.notes,
      });
      toast.success("Call logged");
      loadCalls();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to log call");
    } finally {
      setSaving(false);
    }
  };

  const sendChat = async () => {
    if (!chatMessage.trim()) return;
    try {
      await saleAgentService.sendChat(lead.id, chatMessage.trim());
      toast.success("Message sent");
      setChatMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send message");
    }
  };

  const wa = whatsAppUrl(form.contactNumber);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Contact lead</h3>
            <p className="text-sm text-gray-500">{lead.fullName}</p>
            {lead.linkedUserId ? (
              <p className="text-xs text-emerald-700 mt-1">
                Linked Naseebi user: {lead.linkedUserName || lead.linkedUserEmail}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <FiX />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">Lead details</h4>
            <div>
              <label className="text-xs text-gray-500">Full name</label>
              <input className={inputCls} name="fullName" value={form.fullName} onChange={handleChange} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Contact number</label>
              <input className={inputCls} name="contactNumber" value={form.contactNumber} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Status</label>
                <select className={inputCls} name="maritalStatus" value={form.maritalStatus} onChange={handleChange}>
                  {MARITAL_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Gender</label>
                <select className={inputCls} name="gender" value={form.gender} onChange={handleChange}>
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500">Belongs to (agent)</label>
              <select className={inputCls} name="assignedAgentId" value={form.assignedAgentId} onChange={handleChange}>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Country / City / Notes</label>
              <input className={inputCls} name="countryCity" value={form.countryCity} onChange={handleChange} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Additional notes</label>
              <textarea className={`${inputCls} min-h-[72px]`} name="notes" value={form.notes} onChange={handleChange} />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                disabled={saving}
                onClick={() => saveContact(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
                style={{ backgroundColor: THEME.primary }}
              >
                Save contact
              </button>
              {wa ? (
                <a
                  href={wa}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-600 text-white"
                >
                  WhatsApp
                </a>
              ) : null}
              {form.contactNumber ? (
                <a href={`tel:${form.contactNumber}`} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-white">
                  Call phone
                </a>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <FiPhone style={{ color: THEME.primary }} /> Manual call log
              </h4>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {["hours", "minutes", "seconds"].map((k) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase text-gray-400">{k}</label>
                    <input
                      type="number"
                      min={0}
                      className={inputCls}
                      value={callForm[k]}
                      onChange={(e) => setCallForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }))}
                    />
                  </div>
                ))}
              </div>
              <div className="mb-2">
                <label className="text-[10px] uppercase text-gray-400">Call started</label>
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={callForm.started_at}
                  onChange={(e) => setCallForm((f) => ({ ...f, started_at: e.target.value }))}
                />
              </div>
              <div className="mb-3">
                <label className="text-[10px] uppercase text-gray-400">Call notes</label>
                <input
                  className={inputCls}
                  value={callForm.notes}
                  onChange={(e) => setCallForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. no whats app"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={logManualCall}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-gray-300"
                >
                  Log call only
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => saveContact(true)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Save + log call
                </button>
              </div>
            </div>

            {lead.linkedUserId ? (
              <div className="rounded-xl border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                  <FiMessageCircle style={{ color: THEME.primary }} /> In-app chat
                </h4>
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Type a message…"
                  />
                  <button
                    type="button"
                    onClick={sendChat}
                    className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    Send
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Chat is auto-logged in agent activity.</p>
              </div>
            ) : null}

            <div className="rounded-xl border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
                <FiClock style={{ color: THEME.primary }} /> Call history ({calls.length})
              </h4>
              {callLoading ? (
                <ListSkeleton rows={4} />
              ) : calls.length === 0 ? (
                <p className="text-sm text-gray-400">No calls logged yet.</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto text-sm">
                  {calls.map((c) => (
                    <li key={c.id || c.startedAt} className="border-b border-gray-100 pb-2">
                      <div className="font-medium text-gray-800">
                        {formatCallDuration(c.durationSec || c.duration_sec || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {c.startedAt || c.started_at
                          ? new Date(c.startedAt || c.started_at).toLocaleString()
                          : "—"}
                      </div>
                      {c.notes ? <div className="text-xs text-gray-600">{c.notes}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SaleAgentPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isLead = userRole === "CSR Team Lead";

  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [agentFilter, setAgentFilter] = useState("all");
  const [contactLead, setContactLead] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyLeadForm());
  const [saving, setSaving] = useState(false);
  const [phoneLookup, setPhoneLookup] = useState(null);
  const [phoneLookupError, setPhoneLookupError] = useState("");
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [reassigningId, setReassigningId] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accessRes, leadsRes] = await Promise.all([
        saleAgentService.getAccess(),
        saleAgentService.listLeads({
          search: debouncedSearch,
          page: leadsPage,
          limit: LEADS_PAGE_SIZE,
          ...(agentFilter !== "all" ? { agent_id: agentFilter } : {}),
        }),
      ]);
      setAgents(accessRes.access?.agents || []);
      setLeads(leadsRes.leads || []);
      setLeadsTotal(leadsRes.total ?? 0);
      setLeadsTotalPages(leadsRes.totalPages ?? 1);
    } catch {
      toast.error("Failed to load sale agent leads");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, agentFilter, leadsPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setLeadsPage(1);
  }, [debouncedSearch, agentFilter]);

  useEffect(() => {
    if (!addForm.assignedAgentId && agents[0]?.id) {
      setAddForm((f) => ({ ...f, assignedAgentId: String(agents[0].id) }));
    }
  }, [agents, addForm.assignedAgentId]);

  useEffect(() => {
    const phone = addForm.contactNumber.trim();
    if (!showAdd || phone.replace(/\D/g, "").length < 7) {
      setPhoneLookup(null);
      setPhoneLookupError("");
      return;
    }
    let cancelled = false;
    setPhoneChecking(true);
    const t = setTimeout(async () => {
      try {
        const data = await saleAgentService.lookupPhone(
          phone,
          addForm.assignedAgentId ? Number(addForm.assignedAgentId) : undefined
        );
        if (cancelled) return;
        setPhoneLookup(data);
        setPhoneLookupError("");
        if (data?.matchedUser?.name && !addForm.fullName.trim()) {
          setAddForm((f) => ({ ...f, fullName: data.matchedUser.name }));
        }
      } catch (e) {
        if (cancelled) return;
        setPhoneLookup(null);
        setPhoneLookupError(e?.response?.data?.message || "Phone check failed");
      } finally {
        if (!cancelled) setPhoneChecking(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addForm.contactNumber, addForm.assignedAgentId, showAdd]);

  const handleCreate = async () => {
    if (!addForm.fullName.trim() || !addForm.contactNumber.trim()) {
      toast.error("Name and phone number are required");
      return;
    }
    if (phoneLookupError) {
      toast.error(phoneLookupError);
      return;
    }
    setSaving(true);
    try {
      const res = await saleAgentService.createLead({
        fullName: addForm.fullName.trim(),
        contactNumber: addForm.contactNumber.trim(),
        maritalStatus: addForm.maritalStatus,
        gender: addForm.gender,
        assignedAgentId: Number(addForm.assignedAgentId),
        countryCity: addForm.countryCity,
        notes: addForm.notes,
      });
      toast.success(res.message || "Lead added");
      setShowAdd(false);
      setAddForm(emptyLeadForm());
      setPhoneLookup(null);
      setPhoneLookupError("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  const handleReassign = async (leadId, agentId) => {
    if (!agentId) return;
    setReassigningId(leadId);
    try {
      await saleAgentService.reassignLead(leadId, Number(agentId));
      toast.success("Lead shifted to another CSR");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Reassign failed");
    } finally {
      setReassigningId(null);
    }
  };

  const handleUnassign = async (leadId) => {
    if (!window.confirm("Unassign this lead so another CSR can claim the phone?")) return;
    setUnassigningId(leadId);
    try {
      await saleAgentService.unassignLead(leadId);
      toast.success("Lead unassigned");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unassign failed");
    } finally {
      setUnassigningId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this lead?")) return;
    try {
      await saleAgentService.deleteLead(id);
      toast.success("Lead removed");
      load();
    } catch {
      toast.error("Failed to remove lead");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        toast.error("CSV must have a header row and at least one data row");
        return;
      }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const idx = (names) => headers.findIndex((h) => names.some((n) => h.includes(n)));
      const nameI = idx(["name", "full"]);
      const phoneI = idx(["contact", "phone", "number"]);
      const statusI = idx(["status", "marital"]);
      const genderI = idx(["gender"]);
      const belongsI = idx(["belongs", "agent", "lead"]);
      const cityI = idx(["country", "city"]);
      const parsed = lines.slice(1).map((line) => {
        const cols = line.split(",");
        return {
          fullName: cols[nameI]?.trim() || "",
          contactNumber: cols[phoneI]?.trim() || "",
          maritalStatus: cols[statusI]?.trim() || "not mentioned",
          gender: cols[genderI]?.trim() || "male",
          countryCity: cols[cityI]?.trim() || "",
          belongsTo: cols[belongsI]?.trim() || "",
        };
      }).filter((r) => r.fullName && r.contactNumber);

      const withAgents = parsed.map((row) => {
        const match = agents.find((a) =>
          a.name.toLowerCase().includes((row.belongsTo || "").toLowerCase())
        );
        return {
          fullName: row.fullName,
          contactNumber: row.contactNumber,
          maritalStatus: row.maritalStatus,
          gender: row.gender,
          countryCity: row.countryCity,
          assignedAgentId: match?.id,
        };
      });

      const res = await saleAgentService.importLeads(withAgents);
      toast.success(res.message || "Import complete");
      if (res.failed?.length) toast.error(`${res.failed.length} row(s) failed`);
      load();
    } catch {
      toast.error("Import failed");
    }
    e.target.value = "";
  };

  const stats = useMemo(() => {
    const contacted = leads.filter((l) => l.contactCount > 0).length;
    return { total: leads.length, contacted };
  }, [leads]);

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUser style={{ color: THEME.primary }} /> Sale Agent
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage sale leads · update on contact · auto & manual call logs
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {stats.total} leads · {stats.contacted} contacted
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(isAdmin || isLead) && (
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 hover:bg-gray-200 cursor-pointer">
                <FiUpload /> Import CSV
                <input type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
              </label>
            )}
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiPlus /> Add lead
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex-1 min-w-[220px] relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className={`${inputCls} pl-9`}
              placeholder="Search name, phone, city, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {(isAdmin || isLead) && agents.length > 1 && (
            <select className={`${inputCls} max-w-xs`} value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
              <option value="all">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-orange-50 text-left">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Name</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Contact number</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Gender</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Belongs to</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Country / City</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Contacts</th>
                  <th className="px-3 py-2.5 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-gray-400">
                      No leads yet. Add one or import your sheet.
                    </td>
                  </tr>
                )}
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900">{lead.fullName}</p>
                      {lead.linkedUserId ? (
                        <p className="text-[10px] text-emerald-700">Naseebi user linked</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700">{lead.contactNumber}</td>
                    <td className="px-3 py-2.5 capitalize">{lead.maritalStatus}</td>
                    <td className="px-3 py-2.5 capitalize">{lead.gender || "—"}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium">{lead.assignedAgentName || "—"}</p>
                      {lead.teamLeadName ? (
                        <p className="text-[10px] text-gray-400">Lead: {lead.teamLeadName}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-[160px] truncate" title={lead.countryCity}>
                      {lead.countryCity || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="font-semibold">{lead.contactCount || 0}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setContactLead(lead)}
                          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: THEME.primary }}
                        >
                          Contact
                        </button>
                        {(isAdmin || isLead) && (
                          <>
                            <select
                              className="border border-gray-200 rounded-lg px-1.5 py-1 text-[11px] max-w-[120px] disabled:opacity-50"
                              defaultValue=""
                              disabled={reassigningId === lead.id || unassigningId === lead.id}
                              onChange={(e) => {
                                const v = e.target.value;
                                e.target.value = "";
                                if (v) handleReassign(lead.id, v);
                              }}
                            >
                              <option value="">
                                {reassigningId === lead.id ? "Shifting…" : "Shift to…"}
                              </option>
                              {agents
                                .filter((a) => Number(a.id) !== Number(lead.assignedAgentId))
                                .map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name}
                                  </option>
                                ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleUnassign(lead.id)}
                              disabled={reassigningId === lead.id || unassigningId === lead.id}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                            >
                              {unassigningId === lead.id ? (
                                <>
                                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-700 border-t-transparent" />
                                  Unassigning…
                                </>
                              ) : (
                                "Unassign"
                              )}
                            </button>
                            {reassigningId === lead.id && (
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                                Shifting…
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(lead.id)}
                              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                              title="Remove lead"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <PaginationBar
            page={leadsPage}
            totalPages={leadsTotalPages}
            totalItems={leadsTotal}
            pageSize={LEADS_PAGE_SIZE}
            label="leads"
            onPageChange={setLeadsPage}
          />
        )}
      </div>

      {contactLead ? (
        <ContactModal
          lead={contactLead}
          agents={agents}
          onClose={() => setContactLead(null)}
          onSaved={load}
        />
      ) : null}

      {showAdd ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-900">Add sale lead</h3>
              <button type="button" onClick={() => setShowAdd(false)}>
                <FiX />
              </button>
            </div>
            <div className="space-y-3">
              <input
                className={inputCls}
                placeholder="Full name"
                value={addForm.fullName}
                onChange={(e) => setAddForm((f) => ({ ...f, fullName: e.target.value }))}
              />
              <div>
                <input
                  className={inputCls}
                  placeholder="Phone number (required)"
                  value={addForm.contactNumber}
                  onChange={(e) => setAddForm((f) => ({ ...f, contactNumber: e.target.value }))}
                />
                {phoneChecking ? (
                  <p className="text-[11px] text-gray-400 mt-1">Checking phone…</p>
                ) : phoneLookupError ? (
                  <p className="text-[11px] text-rose-600 mt-1">{phoneLookupError}</p>
                ) : phoneLookup?.userExists ? (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    User found: {phoneLookup.matchedUser?.name || phoneLookup.matchedUser?.email}
                    {phoneLookup.matchedUser?.contactNo ? ` · ${phoneLookup.matchedUser.contactNo}` : ""}
                  </p>
                ) : phoneLookup && !phoneLookup.userExists ? (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Not in Naseebi yet — can still add as external lead
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  className={inputCls}
                  value={addForm.maritalStatus}
                  onChange={(e) => setAddForm((f) => ({ ...f, maritalStatus: e.target.value }))}
                >
                  {MARITAL_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <select
                  className={inputCls}
                  value={addForm.gender}
                  onChange={(e) => setAddForm((f) => ({ ...f, gender: e.target.value }))}
                >
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <select
                className={inputCls}
                value={addForm.assignedAgentId}
                onChange={(e) => setAddForm((f) => ({ ...f, assignedAgentId: e.target.value }))}
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <input
                className={inputCls}
                placeholder="Country / city / notes"
                value={addForm.countryCity}
                onChange={(e) => setAddForm((f) => ({ ...f, countryCity: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || Boolean(phoneLookupError) || phoneChecking}
                onClick={handleCreate}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                Add lead
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
